import { faLocationDot, faTimes, faUndo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import Button from 'components/UI/V2/Button/Button';
import { DropdownSelect, DropdownSelectOption } from 'components/UI/V2/DropdownSelect/DropdownSelect';
import { Select } from 'components/UI/V2/Forms/Select/Select';
import Staff from 'model/Staff';
import moment from 'moment-timezone';
import React from 'react';
import { getZoneLabel, SessionBusinessArea, SessionZone } from 'model/ServiceSession';

export interface SessionStaffSlotRowProps {
    area: SessionBusinessArea;
    staff: Array<Staff>;
    staffId?: number;
    time?: string;
    zone?: SessionZone;
    showZone: boolean;
    showDelete?: boolean;
    readOnly?: boolean;
    hours: Array<DropdownSelectOption<string>>;
    error?: string;
    onChange: (values: { staffId?: number; time?: string }) => void;
    onEditZone: () => void;
    onRemoveZone?: () => void;
    onDelete: () => void;
}

export interface SessionCancelledSlotRowProps {
    area: SessionBusinessArea;
    staffName: string;
    time: string;
    zone?: SessionZone;
    showZone: boolean;
    onRestore?: () => void;
}

export interface SessionStaffSlotsProps {
    showZone: boolean;
    showDelete?: boolean;
    children: React.ReactNode;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        list: {
            'display': 'grid',
            'width': '100%',
            'columnGap': 16,
            'alignItems': 'center',
            'justifyContent': 'space-between',
            'gridTemplateColumns': '211px max-content 18px',

            '&[data-show-zone="true"]': {
                gridTemplateColumns: '211px max-content max-content 18px'
            },
            '&[data-show-delete="false"]': {
                gridTemplateColumns: '211px max-content'
            },
            '&[data-show-zone="true"][data-show-delete="false"]': {
                gridTemplateColumns: '211px max-content max-content'
            },

            [theme.breakpoints.down('xs')]: {
                'columnGap': 12,
                'gridTemplateColumns': 'minmax(0, 1fr) max-content',

                '&[data-show-zone="true"]': {
                    gridTemplateColumns: 'minmax(0, 1fr) max-content'
                },
                '&[data-show-delete="false"]': {
                    gridTemplateColumns: 'minmax(0, 1fr) max-content'
                },
                '&[data-show-zone="true"][data-show-delete="false"]': {
                    gridTemplateColumns: 'minmax(0, 1fr) max-content'
                }
            }
        },
        columnTitle: {
            fontSize: 16,
            fontWeight: 600,
            paddingBottom: 8
        },
        headerBorder: {
            gridColumn: '1 / -1',
            borderBottom: 'solid 1px #222222'
        },
        row: {
            display: 'contents'
        },
        staffSelect: {
            width: 211,
            paddingTop: 16,

            [theme.breakpoints.down('xs')]: {
                width: '100%',
                minWidth: 0
            }
        },
        staffCompact: {
            '&& .MuiSelect-root': {
                height: 40,
                fontSize: 14,
                padding: '0px 0px 0px 14px'
            },
            '&& .MuiInputLabel-formControl': {
                paddingLeft: 14,
                transform: 'translate(0, 12px) scale(1)'
            },
            '& .MuiSelect-icon': {
                right: 12
            }
        },
        time: {
            flexShrink: 0,
            paddingTop: 16
        },
        zoneButton: {
            '&&': {
                width: 100,
                height: 39,
                flexShrink: 0,
                marginTop: 16,
                boxSizing: 'border-box'
            },

            [theme.breakpoints.down('xs')]: {
                gridColumn: 1,
                justifySelf: 'start'
            }
        },
        deleteButton: {
            border: 0,
            fontSize: 18,
            height: 18,
            flexShrink: 0,
            cursor: 'pointer',
            appearance: 'none',
            background: 'none',
            marginTop: 16,

            [theme.breakpoints.down('xs')]: {
                gridColumn: 2,
                justifySelf: 'end'
            }
        },
        zone: {
            gap: 8,
            minWidth: 0,
            boxSizing: 'border-box',
            fontSize: 14,
            display: 'flex',
            color: '#7A7A7A',
            padding: '10px 14px',
            marginTop: 12,
            borderRadius: 8,
            alignItems: 'center',
            backgroundColor: '#F1F1F1',
            gridColumn: '1 / -1'
        },
        zoneLabel: {
            minWidth: 0,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis'
        },
        removeZoneButton: {
            border: 0,
            padding: 0,
            fontSize: 14,
            flexShrink: 0,
            cursor: 'pointer',
            appearance: 'none',
            background: 'none',
            color: '#7A7A7A',
            marginLeft: 'auto'
        },
        rowBorder: {
            gridColumn: '1 / -1',
            borderBottom: 'solid 1px #D4D4D4',
            marginTop: 16
        },
        error: {
            gridColumn: '1 / -1',
            fontSize: 13,
            color: '#D92C2C',
            paddingTop: 8
        },
        cancelledRow: {
            gap: 16,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 66,
            boxSizing: 'border-box',
            padding: '4px 0px',
            borderBottom: 'solid 1px #D4D4D4'
        },
        cancelledInfo: {
            gap: 3,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column'
        },
        cancelledName: {
            fontSize: 16,
            fontWeight: 400,
            lineHeight: '27px'
        },
        cancelledMeta: {
            fontSize: 15,
            color: '#7A7A7A',
            lineHeight: 1
        },
        cancelledList: {
            width: '100%'
        },
        cancelledTitle: {
            fontSize: 16,
            fontWeight: 600,
            paddingBottom: 4
        },
        restoreButton: {
            border: 0,
            width: 38,
            height: 38,
            flexShrink: 0,
            cursor: 'pointer',
            appearance: 'none',
            borderRadius: '50%',
            backgroundColor: '#F1F1F1',
            fontSize: 18,
            color: '#222222',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0
        },
        staffOption: {
            'gap': 8,
            'display': 'flex',
            'alignItems': 'center',

            '& img': {
                width: 22,
                aspectRatio: 1,
                borderRadius: '50%'
            }
        },
        headerSpacer: {
            [theme.breakpoints.down('xs')]: {
                display: 'none'
            }
        }
    })
);

export const SessionStaffSlots: React.FC<SessionStaffSlotsProps> = props => {
    const classes = useStyles();
    const showDelete = props.showDelete !== false;

    return (
        <div
            className={classes.list}
            data-show-zone={props.showZone}
            data-show-delete={showDelete}
        >
            <Typography className={classes.columnTitle}>Staff</Typography>
            <Typography className={classes.columnTitle}>Start Time</Typography>
            {props.showZone && <div className={classes.headerSpacer} />}
            {showDelete && <div className={classes.headerSpacer} />}
            <div className={classes.headerBorder} />
            {props.children}
        </div>
    );
};

export const SessionStaffSlotRow: React.FC<SessionStaffSlotRowProps> = props => {
    const classes = useStyles();

    return (
        <div className={classes.row}>
            <div className={classes.staffSelect}>
                <Select<Staff>
                    placeholder="Select Staff"
                    value={props.staffId ? `${props.staffId}` : ''}
                    wrapperClassName={classes.staffCompact}
                    selectProps={{ disabled: props.readOnly }}
                    renderOption={option => (
                        <div className={classes.staffOption}>
                            <img src={option.data?.person.avatar} />
                            {option.label}
                        </div>
                    )}
                    options={props.staff.map(staff => ({
                        value: `${staff.id}`,
                        label: `${staff.person.firstName} ${staff.person.lastName}`,
                        data: staff
                    }))}
                    onChange={event => props.onChange({ staffId: Number(event.target.value), time: props.time })}
                />
            </div>

            <DropdownSelect<string>
                readOnly={!!props.readOnly}
                label="Start Time"
                value={props.time}
                options={props.hours}
                className={classes.time}
                id={`${props.area}-slot-time-dropdown`}
                onChange={time => props.onChange({ staffId: props.staffId, time })}
            />

            {props.showZone && (
                <Button
                    variant="green"
                    className={classes.zoneButton}
                    id={`${props.area}-slot-zone-button`}
                    disabled={props.readOnly}
                    label={props.zone ? 'Edit Zone' : 'Add Zone'}
                    onClick={props.onEditZone}
                />
            )}

            {!props.readOnly && props.showDelete !== false && (
                <button
                    type="button"
                    aria-label="Remove staff assignment"
                    id={`${props.area}-slot-delete-button`}
                    className={classes.deleteButton}
                    onClick={props.onDelete}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            )}

            {props.showZone && props.zone && (
                <Typography className={classes.zone}>
                    <FontAwesomeIcon icon={faLocationDot} />
                    <span className={classes.zoneLabel}>{getZoneLabel(props.zone)}</span>
                    {!props.readOnly && props.onRemoveZone && (
                        <button
                            type="button"
                            aria-label="Remove zone"
                            id={`${props.area}-slot-remove-zone-button`}
                            className={classes.removeZoneButton}
                            onClick={props.onRemoveZone}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    )}
                </Typography>
            )}

            {props.error && <Typography className={classes.error}>{props.error}</Typography>}

            <div className={classes.rowBorder} />
        </div>
    );
};

export const SessionCancelledSlots: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const classes = useStyles();

    return (
        <div className={classes.cancelledList}>
            <Typography className={classes.cancelledTitle}>Canceled</Typography>
            {children}
        </div>
    );
};

export const SessionCancelledSlotRow: React.FC<SessionCancelledSlotRowProps> = props => {
    const classes = useStyles();
    const zoneLabel = props.showZone ? getZoneLabel(props.zone) : undefined;

    return (
        <div className={classes.cancelledRow}>
            <div className={classes.cancelledInfo}>
                <Typography className={classes.cancelledName}>{props.staffName}</Typography>
                <Typography className={classes.cancelledMeta}>
                    {moment(props.time, 'HH:mm').format('h:mm a')}
                    {zoneLabel ? ' · ' + zoneLabel : ''}
                    {' · Canceled'}
                </Typography>
            </div>
            {props.onRestore && (
                <button
                    type="button"
                    aria-label="Undo"
                    id={`${props.area}-slot-undo-button`}
                    className={classes.restoreButton}
                    onClick={props.onRestore}
                >
                    <FontAwesomeIcon icon={faUndo} />
                </button>
            )}
        </div>
    );
};
